import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Star, Map, User } from 'lucide-react';
import { toast } from 'sonner';

export default function GroupRideRatingModal({ rideId, ride, onClose, onRated }) {
  const [hostRating, setHostRating] = useState(0);
  const [hostHover, setHostHover] = useState(0);
  const [routeRating, setRouteRating] = useState(0);
  const [routeHover, setRouteHover] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (hostRating === 0) {
      toast.error('Please rate the host');
      return;
    }

    setLoading(true);
    try {
      // Rate the host
      await axios.post(`/ratings/${rideId}`, {
        rated_user_id: ride.creator_id,
        stars: hostRating,
        comment: comment,
        rating_type: 'host'
      });

      // If route rating provided, we could store it separately
      // For now, we'll include it in the host rating comment
      if (routeRating > 0) {
        // Route rating could be stored in a separate collection
        // For MVP, we'll just acknowledge it
        console.log('Route rating:', routeRating);
      }

      onRated();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('You have already rated this ride');
        onClose();
      } else {
        toast.error('Failed to submit rating');
      }
    } finally {
      setLoading(false);
    }
  };

  const StarRating = ({ rating, hover, setRating, setHover, label, icon: Icon }) => (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-purple-500" />
        <span className="text-sm font-medium text-foreground">{label}</span>
      </div>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star
              className={`w-8 h-8 ${
                star <= (hover || rating)
                  ? 'text-yellow-500 fill-yellow-500'
                  : 'text-gray-600'
              }`}
            />
          </button>
        ))}
      </div>
      {rating > 0 && (
        <p className="text-xs text-muted-foreground">
          {rating === 1 && 'Poor'}
          {rating === 2 && 'Fair'}
          {rating === 3 && 'Good'}
          {rating === 4 && 'Great'}
          {rating === 5 && 'Excellent'}
        </p>
      )}
    </div>
  );

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            Rate Your Group Ride
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Host Rating */}
          <StarRating
            rating={hostRating}
            hover={hostHover}
            setRating={setHostRating}
            setHover={setHostHover}
            label="Rate the Host"
            icon={User}
          />

          {/* Route Rating */}
          <StarRating
            rating={routeRating}
            hover={routeHover}
            setRating={setRouteRating}
            setHover={setRouteHover}
            label="Rate the Route (Optional)"
            icon={Map}
          />

          {/* Comment */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Comments (Optional)
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience..."
              rows={3}
            />
          </div>

          <p className="text-xs text-muted-foreground">
            Your rating helps build trust in the cycling community. Host ratings affect their reputation score.
          </p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading || hostRating === 0} className="flex-1">
            {loading ? 'Submitting...' : 'Submit Rating'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
